import { useEffect, useMemo, useRef, useState } from 'react';
import { act } from '../lib/actions';
import { api, upload } from '../lib/api';
import { useNova } from '../lib/store';
import type { ChatView, FileView, WaTemplate } from '../lib/types';
import { Drawer, Field, inputClass } from './admin/kit';
import { Button } from './ui';

const BLANK = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** A template's blanks: the text header's and the body's, in order. */
export function blanksOf(t: WaTemplate): string[] {
  const out: string[] = [];
  for (const c of t.components) {
    if (c.type === 'BODY' || (c.type === 'HEADER' && c.format === 'TEXT')) for (const m of (c.text ?? '').matchAll(BLANK)) if (!out.includes(m[1])) out.push(m[1]);
  }
  return out.every((b) => /^\d+$/.test(b)) ? out.sort((a, b) => Number(a) - Number(b)) : out;
}

/** The message as the customer will see it. */
export function preview(t: WaTemplate, values: Record<string, string>): string {
  const fill = (s: string) => s.replace(BLANK, (_, k: string) => values[k]?.trim() || `{{${k}}}`);
  const header = t.components.find((c) => c.type === 'HEADER' && c.format === 'TEXT')?.text;
  const body = t.components.find((c) => c.type === 'BODY')?.text;
  const footer = t.components.find((c) => c.type === 'FOOTER')?.text;
  const buttons = (t.components.find((c) => c.type === 'BUTTONS')?.buttons ?? []).map((b) => `[${b.text}]`).join(' ');
  return [header && fill(header), body && fill(body), footer, buttons].filter(Boolean).join('\n\n');
}

/** Headers that carry a file, what they take, and what the file input accepts. */
export const MEDIA_HEADERS: Record<string, { label: string; takes: string; accept: string }> = {
  IMAGE: { label: 'Image', takes: 'a JPEG or PNG image up to 5 MB', accept: '.jpg,.jpeg,.png' },
  VIDEO: { label: 'Video', takes: 'an MP4 video up to 16 MB', accept: '.mp4' },
  DOCUMENT: { label: 'Document', takes: 'a PDF up to 25 MB', accept: '.pdf' },
};

export const isMedia = (t: WaTemplate | null) => Boolean(t?.headerFormat && MEDIA_HEADERS[t.headerFormat]);

/** Picks a file for an image, video or document header; uploaded to Nova at once. */
export function HeaderFilePicker({ format, file, fallback, onFile, label }: { format: string; file: FileView | null; fallback?: FileView | null; onFile: (f: FileView | null) => void; label?: string }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const rule = MEDIA_HEADERS[format];
  const shown = file ?? fallback ?? null;
  async function pick(f: File | undefined) {
    if (!f) return;
    setBusy(true);
    const up = await act(() => upload<FileView>('/whatsapp/header-files', f));
    setBusy(false);
    if (input.current) input.current.value = '';
    if (up) onFile(up);
  }
  return (
    <div className="mb-4" aria-label={label ?? `Header ${rule.label.toLowerCase()}`}>
      <p className="mb-1 text-[12px] font-medium text-ink-2">{label ?? `Header ${rule.label.toLowerCase()}`}</p>
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-line p-2 text-sm">
        <span className={shown ? 'text-ink' : 'text-ink-3'}>
          {shown ? shown.filename : `None yet: ${rule.takes}`}
          {shown && !file && fallback ? <span className="text-[12px] text-ink-3"> (the template's own)</span> : null}
        </span>
        <input ref={input} type="file" accept={rule.accept} className="hidden" aria-label={`Choose the ${rule.label.toLowerCase()}`} onChange={(e) => void pick(e.target.files?.[0])} />
        <Button tone="quiet" className="ml-auto px-2.5 py-1 text-xs" disabled={busy} onClick={() => input.current?.click()}>
          {busy ? 'Uploading…' : shown ? 'Choose another' : 'Choose a file'}
        </Button>
        {file ? (
          <Button tone="ghost" className="px-2 py-1 text-xs" onClick={() => onFile(null)}>
            Remove
          </Button>
        ) : null}
      </div>
      <p className="mt-1 text-[11px] text-ink-3">WhatsApp takes {rule.takes}.</p>
    </div>
  );
}

export function useTemplates(accountId: string | null | undefined): WaTemplate[] | null {
  const [list, setList] = useState<WaTemplate[] | null>(null);
  useEffect(() => {
    if (!accountId) return;
    let live = true;
    api<WaTemplate[]>('GET', `/whatsapp/templates?accountId=${accountId}`)
      .then((l) => live && setList(l))
      .catch(() => live && setList([]));
    return () => {
      live = false;
    };
  }, [accountId]);
  return list;
}

/** Choosing an approved template and filling its blanks, with what the customer will see. */
function TemplateForm({
  templates,
  values,
  onValues,
  chosen,
  onChoose,
  file,
  onFile,
}: {
  templates: WaTemplate[] | null;
  values: Record<string, string>;
  onValues: (v: Record<string, string>) => void;
  chosen: WaTemplate | null;
  onChoose: (t: WaTemplate | null) => void;
  file: FileView | null;
  onFile: (f: FileView | null) => void;
}) {
  const usable = (templates ?? []).filter((t) => t.sendable);
  const blanks = chosen ? blanksOf(chosen) : [];
  return (
    <>
      <Field
        label="Template"
        hint={templates && !usable.length ? 'No approved templates for this number yet. An admin adds them under Admin > Channels > WhatsApp.' : 'Approved by Meta. Templates are the only messages WhatsApp allows 24 hours after the customer last wrote.'}
      >
        {(id) => (
          <select id={id} className={inputClass} value={chosen?.id ?? ''} onChange={(e) => onChoose(usable.find((t) => t.id === e.target.value) ?? null)}>
            <option value="">{templates ? 'Choose a template' : 'Loading…'}</option>
            {usable.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.language})
              </option>
            ))}
          </select>
        )}
      </Field>
      {chosen && isMedia(chosen) ? <HeaderFilePicker format={chosen.headerFormat!} file={file} fallback={chosen.headerFile} onFile={onFile} /> : null}
      {blanks.map((b) => (
        <Field key={b} label={`{{${b}}}`}>
          {(id) => <input id={id} className={inputClass} value={values[b] ?? ''} maxLength={1024} onChange={(e) => onValues({ ...values, [b]: e.target.value })} />}
        </Field>
      ))}
      {chosen ? (
        <div className="mb-4">
          <p className="mb-1 text-[12px] font-medium text-ink-2">The customer will see</p>
          <div className="whitespace-pre-wrap rounded-lg border border-line bg-[#e7f8ee] p-3 text-sm text-[#1a1f1d]" aria-label="Template preview">
            {isMedia(chosen) ? <p className="mb-2 rounded border border-[#1a1f1d]/15 bg-white/60 px-2 py-1.5 text-[12px]">{MEDIA_HEADERS[chosen.headerFormat!].label}: {(file ?? chosen.headerFile)?.filename ?? 'not chosen yet'}</p> : null}
            {preview(chosen, values)}
          </div>
        </div>
      ) : null}
    </>
  );
}

/** A template in an open WhatsApp chat. */
export function SendTemplate({ chat, onClose }: { chat: ChatView; onClose: () => void }) {
  const templates = useTemplates(chat.accountId);
  const [chosen, setChosen] = useState<WaTemplate | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [file, setFile] = useState<FileView | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = chosen && blanksOf(chosen).every((b) => values[b]?.trim()) && (!isMedia(chosen) || file || chosen.headerFile);
  async function send() {
    setBusy(true);
    const ok = await act(() => api('POST', `/chats/${chat.interactionId}/template`, { templateId: chosen!.id, values, ...(file ? { headerFileId: file.id } : {}) }), 'Template sent');
    setBusy(false);
    if (ok) onClose();
  }
  return (
    <Drawer
      title={`WhatsApp template to ${chat.customerName ?? chat.handle ?? 'the customer'}`}
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || !ready} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send template'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      <TemplateForm templates={templates} values={values} onValues={setValues} chosen={chosen} onChoose={(t) => (setChosen(t), setValues({}), setFile(null))} file={file} onFile={setFile} />
    </Drawer>
  );
}

interface WaNumber {
  id: string;
  name: string;
  display_phone: string;
}

/** New > New WhatsApp message: write to a customer first, with a template; the chat then opens in Work. */
export function StartWhatsApp({ onClose }: { onClose: () => void }) {
  const [numbers, setNumbers] = useState<WaNumber[] | null>(null);
  const [accountId, setAccountId] = useState('');
  const [to, setTo] = useState('');
  const templates = useTemplates(accountId);
  const [chosen, setChosen] = useState<WaTemplate | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [file, setFile] = useState<FileView | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api<WaNumber[]>('GET', '/whatsapp/numbers')
      .then((n) => {
        setNumbers(n);
        if (n[0]) setAccountId((a) => a || n[0].id);
      })
      .catch(() => setNumbers([]));
  }, []);
  const digits = to.replace(/\D/g, '');
  const ready = useMemo(() => accountId && digits.length >= 8 && digits.length <= 15 && chosen && blanksOf(chosen).every((b) => values[b]?.trim()) && (!isMedia(chosen) || file || chosen.headerFile), [accountId, digits, chosen, values, file]);
  async function send() {
    setBusy(true);
    const view = await act(() => api<ChatView>('POST', '/whatsapp/conversations', { accountId, to, templateId: chosen!.id, values, ...(file ? { headerFileId: file.id } : {}) }), 'WhatsApp message sent');
    setBusy(false);
    if (!view) return;
    const st = useNova.getState();
    st.set({ chats: { ...st.chats, [view.interactionId]: view }, selectedId: view.interactionId, view: 'cockpit' });
    onClose();
  }
  return (
    <Drawer
      title="New WhatsApp message"
      open
      onClose={onClose}
      footer={
        <>
          <Button tone="primary" className="px-4 py-2" disabled={busy || !ready} onClick={() => void send()}>
            {busy ? 'Sending…' : 'Send'}
          </Button>
          <Button tone="ghost" className="px-3 py-2" onClick={onClose}>
            Cancel
          </Button>
        </>
      }
    >
      {numbers && numbers.length === 0 ? (
        <p className="text-sm text-ink-2">There is no WhatsApp number to write from yet. An admin connects one under Admin &gt; Channels &gt; WhatsApp.</p>
      ) : (
        <>
          <Field label="From">
            {(id) => (
              <select id={id} className={inputClass} value={accountId} onChange={(e) => (setAccountId(e.target.value), setChosen(null), setValues({}))}>
                {(numbers ?? []).map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.name} ({n.display_phone})
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="To" hint="The customer's WhatsApp number with its country code, for example +254 712 345 678">
            {(id) => <input id={id} inputMode="tel" className={`${inputClass} tabular`} value={to} onChange={(e) => setTo(e.target.value)} />}
          </Field>
          <TemplateForm templates={accountId ? templates : []} values={values} onValues={setValues} chosen={chosen} onChoose={(t) => (setChosen(t), setValues({}), setFile(null))} file={file} onFile={setFile} />
          <p className="text-[12px] text-ink-3">WhatsApp lets a business start a conversation only with an approved template. When the customer answers, the chat goes on as usual.</p>
        </>
      )}
    </Drawer>
  );
}
